import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { Icons } from './Icons';

const DAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const pretty = (d: Date) => d.toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric' });

// Fully custom calendar — never the native date input. Disables past dates.
export function DateField({
  value,
  onChange,
  placeholder = 'Choose a date',
}: {
  value: string | null; // ISO yyyy-mm-dd
  onChange: (v: string | null) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const selDate = value ? new Date(value + 'T00:00:00') : null;
  const [view, setView] = useState(() => new Date(selDate ?? today));
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const year = view.getFullYear();
  const month = view.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (Date | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const maxView = new Date(today.getFullYear(), today.getMonth() + 6, 1);

  return (
    <div ref={rootRef} className="relative">
      <div
        className={`flex w-full items-center justify-between gap-2 rounded-2xl border bg-white pr-2 font-body transition-colors ${
          open ? 'border-terracotta shadow-glow' : 'border-stone hover:border-terracotta/50'
        }`}
      >
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex flex-1 cursor-pointer items-center gap-2.5 px-4 py-3.5 text-left"
        >
          <Icons.calendar width={18} height={18} className="text-plum/40" />
          <span className={selDate ? 'text-plum' : 'text-plum/45'}>{selDate ? pretty(selDate) : placeholder}</span>
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="cursor-pointer rounded-full p-1.5 text-plum/40 transition-colors hover:text-plum"
            aria-label="Clear date"
          >
            <Icons.close width={15} height={15} />
          </button>
        )}
      </div>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0, y: 6, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 6, scale: 0.98 }}
              transition={{ duration: 0.16 }}
              className="absolute left-0 top-[calc(100%+8px)] z-50 w-[min(320px,calc(100vw-2.5rem))] rounded-2xl border border-stone bg-white p-4 shadow-lift"
            >
              <div className="mb-3 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setView(new Date(year, month - 1, 1))}
                  disabled={year === today.getFullYear() && month === today.getMonth()}
                  className="cursor-pointer rounded-full p-1.5 text-plum/60 transition-colors hover:bg-mist disabled:cursor-not-allowed disabled:opacity-30"
                  aria-label="Previous month"
                >
                  <Icons.arrowLeft width={18} height={18} />
                </button>
                <span className="font-display text-lg font-semibold text-plum">
                  {MONTHS[month]} {year}
                </span>
                <button
                  type="button"
                  onClick={() => setView(new Date(year, month + 1, 1))}
                  disabled={view >= maxView}
                  className="cursor-pointer rounded-full p-1.5 text-plum/60 transition-colors hover:bg-mist disabled:cursor-not-allowed disabled:opacity-30"
                  aria-label="Next month"
                >
                  <Icons.arrowRight width={18} height={18} />
                </button>
              </div>
              <div className="mb-1 grid grid-cols-7">
                {DAYS.map((d, i) => (
                  <span key={i} className="py-1 text-center font-body text-xs font-semibold text-plum/40">
                    {d}
                  </span>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-0.5">
                {cells.map((d, i) => {
                  if (!d) return <span key={i} />;
                  const past = d < today;
                  const isSel = selDate && iso(d) === iso(selDate);
                  const isToday = iso(d) === iso(today);
                  return (
                    <button
                      key={i}
                      type="button"
                      disabled={past}
                      onClick={() => {
                        onChange(iso(d));
                        setOpen(false);
                      }}
                      className={`aspect-square rounded-full font-body text-sm transition-colors ${
                        isSel
                          ? 'bg-terracotta font-semibold text-blush'
                          : past
                            ? 'cursor-not-allowed text-plum/25'
                            : 'cursor-pointer text-plum hover:bg-terracotta/10'
                      } ${isToday && !isSel ? 'ring-1 ring-inset ring-sage' : ''}`}
                    >
                      {d.getDate()}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
